import { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {toast} from 'react-toastify';
import { Plus, Folder, Search, ChevronRight, GitBranch } from 'lucide-react';
import { io } from 'socket.io-client';
import automacoesService from './services/automacoesService';
import pastasService from './services/pastasService';
import FolderCard from './components/FolderCard';
import CampaignRow from './components/CampaignRow';
import CreateCampaignModal from './components/CreateCampaignModal';
import { SidebarContext } from '../../shared/context/SidebarContext';


export default function AutomacoesList() {
  const navigate = useNavigate();
  const { isOpen } = useContext(SidebarContext);

  const [campanhas, setCampanhas] = useState([]);
  const [pastas, setPastas] = useState([]);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pastaSelecionada, setPastaSelecionada] = useState(null);
  const [ordenacao, setOrdenacao] = useState('criado');

  const carregar = () => Promise.all([
    automacoesService.listar(),
    pastasService.listar(),
  ])
    .then(([cs, ps]) => {
      setCampanhas(Array.isArray(cs.data) ? cs.data : []);
      setPastas(Array.isArray(ps.data) ? ps.data : []);
    })
    .catch((e) => {
      console.error(e);
      toast.error('Erro ao carregar fluxos');
    });

  useEffect(() => { carregar(); }, []);

  const reloadTimer = useRef(null);
  useEffect(() => {
    const socket = io({ path: '/api/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });
    const scheduleReload = () => {
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(carregar, 1000);
    };
    socket.on('connect', () => socket.emit('join-empresa'));
    socket.on('reconnect', () => socket.emit('join-empresa'));
    socket.on('automacao:enrolled', (data) => {
      if (data?.count > 0) toast.info(`${data.count} lead${data.count === 1 ? '' : 's'} entrou na automação`);
      scheduleReload();
    });
    socket.on('automacao:completed', scheduleReload);
    socket.on('automacao:exits', scheduleReload);
    socket.on('automacao:travados', (data) => {
      if (data?.count > 0) toast.warn(`${data.count} lead${data.count === 1 ? '' : 's'} travado${data.count === 1 ? '' : 's'} há > 10min`);
    });
    socket.on('automacao-stats', scheduleReload);

    const fallback = setInterval(carregar, 30000);

    return () => {
      clearTimeout(reloadTimer.current);
      clearInterval(fallback);
      try { socket.emit('leave-empresa'); } catch { /* noop */ }
      socket.close();
    };
  }, []);

  const handleNovaPasta = async () => {
    const nome = prompt('Nome da pasta:');
    if (!nome) return;
    try {
      await pastasService.criar({ nome });
      toast.success('Pasta criada');
      carregar();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao criar pasta');
    }
  };

  const handleAbrirPasta = (pasta) => {
    setPastaSelecionada((atual) => (atual?.id === pasta.id ? null : pasta));
  };

  const handleExcluirCampanha = async (campanha) => {
    try {
      await automacoesService.excluir(campanha.id);
      toast.success('Campanha excluída');
      carregar();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || 'Erro ao excluir campanha');
    }
  };

  // Play/Pause da listagem. Ativar pode ser recusado pelo backend (regra do
  // nó Anti-rajada em automações com WhatsApp não-oficial) — mostra o motivo.
  const handleToggleStatus = async (campanha) => {
    const novo = campanha.status === 'ativa' ? 'pausada' : 'ativa';
    try {
      await automacoesService.atualizarStatus(campanha.id, novo);
      toast.success(novo === 'ativa' ? `"${campanha.nome}" ativada` : `"${campanha.nome}" pausada`);
      setCampanhas((cs) => cs.map((c) => (c.id === campanha.id ? { ...c, status: novo } : c)));
      carregar();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || 'Erro ao alterar status', { autoClose: 8000 });
    }
  };

  const handleExcluirPasta = async (pasta) => {
    try {
      await pastasService.excluir(pasta.id);
      toast.success('Pasta excluída');
      if (pastaSelecionada?.id === pasta.id) setPastaSelecionada(null);
      carregar();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || 'Erro ao excluir pasta');
    }
  };

  const filtered = campanhas
    .filter((c) => (c.nome || '').toLowerCase().includes(search.toLowerCase()))
    .filter((c) => (pastaSelecionada ? c.pasta_id === pastaSelecionada.id : true));

  const ordenadas = [...filtered].sort((a, b) => {
    if (ordenacao === 'nome') return (a.nome || '').localeCompare(b.nome || '');
    if (ordenacao === 'leads') return (b.leads_ativos || 0) - (a.leads_ativos || 0);
    if (ordenacao === 'criado') {
      // Mais nova primeiro; desempate por id pra não embaralhar clones criados no mesmo segundo.
      const diff = new Date(b.criado_em || 0).getTime() - new Date(a.criado_em || 0).getTime();
      return diff !== 0 ? diff : (b.id || 0) - (a.id || 0);
    }
    const aDate = new Date(a.atualizado_em || a.criado_em || 0).getTime();
    const bDate = new Date(b.atualizado_em || b.criado_em || 0).getTime();
    return bDate - aDate;
  });

  return (
    <div className="min-h-screen text-ink-1 flex relative z-0">
      <div className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full max-w-full mb-16 sm:mb-0`}>

        <div className="px-5 sm:px-8 pt-8 pb-12 max-w-[1600px] mx-auto">

          <header className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 animate-fade-in">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-positive-400 animate-glow-pulse" />
                <span className="label-overline text-positive-400/80">Tempo real</span>
              </div>
              <h1 className="display-2 flex items-center gap-3">
                Fluxos
                <GitBranch className="h-7 w-7 text-brand-400" strokeWidth={2.2} />
              </h1>
              <p className="text-ink-3 text-sm">
                <span className="tabular-nums text-ink-2 font-medium">{campanhas.length}</span> campanha{campanhas.length === 1 ? '' : 's'} em <span className="tabular-nums text-ink-2 font-medium">{pastas.length}</span> pasta{pastas.length === 1 ? '' : 's'}.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-4 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar campanha..."
                  className="input-field !py-2 !text-sm pl-10 sm:w-72"
                />
              </div>
              <button onClick={handleNovaPasta} className="btn-ghost !py-2">
                <Folder className="h-4 w-4" /> Nova Pasta
              </button>
              <button onClick={() => setCreateOpen(true)} className="btn-primary !py-2 !px-4">
                <Plus className="h-4 w-4" /> Nova Campanha
              </button>
            </div>
          </header>

          <section className="mb-8 animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="label-overline">Pastas</span>
                {pastaSelecionada && (
                  <span className="chip-brand">
                    <Folder className="h-3 w-3" />
                    {pastaSelecionada.nome}
                  </span>
                )}
              </div>
              {pastaSelecionada ? (
                <button
                  onClick={() => setPastaSelecionada(null)}
                  className="text-xs text-ink-4 hover:text-ink-2 transition"
                >
                  Limpar filtro
                </button>
              ) : (
                <button className="text-xs text-ink-4 hover:text-ink-2 flex items-center gap-1 transition">
                  Ver todas <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {pastas.map((p) => (
                <FolderCard
                  key={p.id}
                  folder={p}
                  onClick={handleAbrirPasta}
                  onDelete={handleExcluirPasta}
                />
              ))}
              <button
                onClick={handleNovaPasta}
                className="rounded-2xl border-2 border-dashed border-white/[0.08] hover:border-brand-500/40 hover:bg-brand-500/5 grid place-items-center text-ink-4 hover:text-brand-300 text-sm gap-2 py-6 transition-all duration-200 ease-out-expo"
              >
                <Plus className="h-5 w-5" /> Nova pasta
              </button>
            </div>
          </section>

          <section className="animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <span className="label-overline">Campanhas</span>
              <div className="flex items-center gap-2">
                <span className="text-2xs uppercase tracking-wider text-ink-4">Ordenar</span>
                <select
                  value={ordenacao}
                  onChange={(e) => setOrdenacao(e.target.value)}
                  className="bg-surface-3 border border-white/[0.08] hover:border-white/[0.14] rounded-lg text-xs text-ink-2 px-2.5 py-1.5 focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/15 outline-none transition cursor-pointer"
                >
                  <option value="criado">Mais novas primeiro</option>
                  <option value="atualizado">Atualizado recentemente</option>
                  <option value="nome">Nome</option>
                  <option value="leads">Mais leads</option>
                </select>
              </div>
            </div>

            <div className="surface-card p-2">
              <div className="grid grid-cols-12 gap-4 px-4 py-2 text-2xs uppercase tracking-wider text-ink-5 font-semibold border-b border-white/[0.04]">
                <div className="col-span-5">Campanha</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2">Sequência</div>
                <div className="col-span-1">Enviadas</div>
                <div className="col-span-2">Conversão</div>
                <div className="col-span-1 text-right" />
              </div>

              {ordenadas.length === 0 ? (
                <div className="py-12 text-center text-ink-4 text-sm">
                  Nenhuma campanha encontrada.
                </div>
              ) : (
                <div>
                  {ordenadas.map((c) => (
                    <CampaignRow
                      key={c.id}
                      c={c}
                      onOpen={(item) => navigate(`/automacoes/${item.id}/editar`)}
                      onDelete={handleExcluirCampanha}
                      onToggleStatus={handleToggleStatus}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <CreateCampaignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pastaId={pastaSelecionada?.id || null}
        onCreated={carregar}
      />
    </div>
  );
}
